import { Button, Divider } from "@mui/material";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import AsyncSelectScroll from "@/components/AsyncSelectScroll";
import Flex from "../../../../../components/Flex";
import Input from "../../../../../components/Input";
import LoadingOverlay from "../../../../../components/LoadingOverlay";
import SideSheetDrawer from "../../../../../components/SidesheetDrawer";
import Text from "../../../../../components/Text";
import DropZone from "../../../../../components/app/DropZone";
import { useDebounce } from "../../../../../hooks/use-debounce";
import { DisclosureHost } from "../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { useGetPatient } from "../../../../../queries/use-get-patient-uuid";
import { useProviderGroupSenderEmail } from "../../../../../queries/practice-setting/configuration/use-provider-group-sender-email";
import { usePatientEmailSend } from "../../../../../queries/patient/use-patient-email-send";
import { useEmailSend } from "../../../../../queries/template/email-template/use-patient-email-send";
import { useRosTemplateById } from "../../../../../queries/template/new-flow/use-new-template-by-id-list";
import { useNewTemplateList } from "../../../../../queries/template/new-flow/use-new-template-list";
import { GetAllClaimTemplateTypesEnum } from "../../../../../services";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "../../../../../utils/utils";
import { EmailTo } from "./EmailTo";
import * as Yup from "yup";
import { isEmpty } from "lodash";
import RichTextEditor from "../../../../../components/richTextEditor/RichTextEditor";
import { useQueryClient } from "@tanstack/react-query";

interface SendBulkEmailModalProps {
  disClosureHost: DisclosureHost;
  disableAddPatient?: boolean;
  /** Keeps the To recipients read-only — see EmailTo. */
  lockRecipients?: boolean;
  /**
   * Shows the attachment drop zone. A mail carrying a file is sent through the
   * multipart endpoint, which addresses by email address rather than patient
   * uuid; see handleSendMail.
   */
  allowAttachment?: boolean;
  onSave?: () => void;
}

const SendBulkEmailModal = ({
  disClosureHost,
  disableAddPatient = false,
  lockRecipients = false,
  allowAttachment = false,
  onSave,
}: SendBulkEmailModalProps) => {
  const [selectedTemplate, setSelectedTemplate] = useState<any>();
  const [searchTemplate, setSearchTemplate] = useDebounce<string>("");
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const {
    data: templateList,
    isLoading,
    isRefetching,
    isFetching,
  } = useNewTemplateList({
    pagination,
    searchKey: searchTemplate,
    templateType: GetAllClaimTemplateTypesEnum.Email,
    xTENANTID: getXtenantId(),
  });

  const [patients, setPatients] = useState<
    { uuid: string; name: string; claimUuid?: string }[]
  >(disClosureHost?.data ?? []);
  const { mutateAsync: sendMail } = useEmailSend();
  const { mutateAsync: sendMailWithAttachment } = usePatientEmailSend();

  // Only needed to address an attachment mail, so both are skipped entirely
  // unless this composer offers attachments.
  const { data: recipientDetails } = useGetPatient(
    patients?.[0]?.uuid,
    !allowAttachment
  );
  const { data: senderConfig }: any = useProviderGroupSenderEmail({
    enabled: allowAttachment,
  });

  const toEmail = recipientDetails?.email;
  // Same access path the Practice Settings screen reads the verified sender
  // from (EmailIdVerificationForm).
  const fromEmail = senderConfig?.data?.emailId;

  const { data: selectedTemplateData, isLoading: selectedTemplateLoading } =
    useRosTemplateById({
      rosTemplateUuid: selectedTemplate?.value,
      xTENANTID: getXtenantId(),
    });

  const showSnackbar = useSnackbar();

  useEffect(() => setPatients(disClosureHost?.data), [disClosureHost?.data]);

  useEffect(() => {
    if (selectedTemplateData?.data && selectedTemplate?.value) {
      formik.setFieldValue(
        "description",
        selectedTemplateData?.data?.description
      );
      formik.setFieldValue("subject", selectedTemplateData?.data?.subject);
    }
  }, [selectedTemplateData?.data, selectedTemplate?.value]);

  const closeAndReset = () => {
    setSelectedTemplate(undefined);
    disClosureHost.close();
    formik.resetForm();
  };

  const queryClient = useQueryClient();

  const onSent = (res: any) => {
    showSnackbar(getSuccessSnackbar(res));
    onSave?.();
    closeAndReset();
    queryClient.invalidateQueries({ queryKey: ["patient-portal-emails"] });
  };

  const handleSendMail = (v: any) => {
    if (isEmpty(patients)) {
      showSnackbar({
        type: "error",
        message: "Please add at least one patient",
      });

      return;
    }

    // An attachment has to go through send-email-with-attachment, which is
    // multipart and addresses the mail by email address. Without a file the
    // JSON send-email is kept, so the normal path keeps its patientUuids and
    // the claimUuid that links an Email Claim back to its claim.
    if (v?.attachment) {
      if (patients.length > 1) {
        showSnackbar({
          type: "error",
          message: "An attachment can only be sent to one patient at a time",
        });
        return;
      }
      if (!toEmail) {
        showSnackbar({
          type: "error",
          message: "This patient has no email address on file",
        });
        return;
      }
      if (!fromEmail) {
        showSnackbar({
          type: "error",
          message:
            "Verify a sender email under Practice Settings before sending an attachment",
        });
        return;
      }

      sendMailWithAttachment({
        toEmail,
        fromEmail,
        subject: v.subject,
        description: v.description,
        attachment: v.attachment,
        xTENANTID: getXtenantId(),
      })
        .then(onSent)
        .catch((err) => showSnackbar(getErrorSnackbar(err)));

      return;
    }

    const payload = {
      patientUuids: patients?.map((it) => it?.uuid),
      claimUuid: patients?.[0]?.claimUuid || undefined,
      subject: v.subject,
      description: v.description,
    };

    sendMail({ emailToPatient: payload, xTENANTID: getXtenantId() })
      .then(onSent)
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  };

  const formik = useFormik({
    initialValues: {
      subject: "",
      description: "",
      attachment: undefined as File | undefined,
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      subject: Yup.string().required().label("Subject"),
      description: Yup.string().required().label("Description"),
    }),
    onSubmit: handleSendMail,
  });

  const handleRemovePatient = (ind: number) => {
    setPatients((prev) => prev?.toSpliced(ind, 1));
  };

  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const handleAddPatient = (patient: any) => {
    setSelectedPatient(patient);
    if (patients?.some((it) => it?.uuid === patient?.value)) {
      showSnackbar({
        type: "error",
        message: "Patient already added",
      });
      setSelectedPatient(null);
      return;
    } else {
      setPatients((prev) => {
        return [
          ...(prev ?? []),
          { uuid: patient?.value, name: patient?.label },
        ];
      });
    }

    setTimeout(() => setSelectedPatient(null), 0);
  };

  return (
    <SideSheetDrawer
      open={disClosureHost.opened}
      w="872px"
      title="New Mail"
      onClose={closeAndReset}
      footerActions={
        <Button variant="contained" onClick={formik.submitForm}>
          Send
        </Button>
      }
    >
      <LoadingOverlay isLoading={selectedTemplateLoading} />

      <Flex flexDirection={"column"}>
        <Divider sx={{ marginBottom: 2, borderColor: "gray" }} />
        <Flex width={"100%"}>
          <EmailTo
            patientList={patients}
            handleRemovePatient={handleRemovePatient}
            disableAddPatient={disableAddPatient}
            lockRecipients={lockRecipients}
            selectedPatient={selectedPatient}
            handleAddPatient={handleAddPatient}
          />
        </Flex>

        <Divider sx={{ marginY: 2, borderColor: "gray" }} />
        <AsyncSelectScroll
          loading={isLoading || isRefetching || isFetching}
          placeholder="Select Template"
          onSearch={(v) => {
            setSearchTemplate(v);
            setPagination((p) => ({ ...p, page: 0 }));
          }}
          options={templateList?.data?.content
            // ?.filter((it) => !!it?.emailSmsTemplateId)
            ?.map((it) => ({
              value: it?.uuid,
              label: it?.templateName,
            }))}
          value={selectedTemplate}
          onChange={(val) => setSelectedTemplate(val)}
          pagination={pagination}
          setPagination={setPagination}
          totalElements={templateList?.data?.page?.totalElements}
        />
        <Divider sx={{ marginY: 2, borderColor: "gray" }} />

        <Flex width={"100%"} gap={2} alignItems={"center"}>
          <Text color={"#1A1A1A66"}>Subject</Text>
          <Input
            required
            label=""
            textFieldProps={{
              fullWidth: true,
              name: "subject",
              onBlur: formik.handleBlur,
              value: formik.values?.subject,
              onChange: formik.handleChange,
              placeholder: "Enter Subject",
            }}
            error={formik.touched?.subject && (formik.errors?.subject as any)}
          />
        </Flex>
        <Divider sx={{ marginY: 2, borderColor: "gray" }} />
        <RichTextEditor
          name="description"
          value={formik.values?.description}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched?.description && (formik.errors?.description as any)
          }
          placeholder="Enter Description"
          minRows={allowAttachment ? 12 : 20}
        />

        {allowAttachment && (
          <>
            <Divider sx={{ marginY: 2, borderColor: "gray" }} />
            <DropZone
              supportedFile="JPEG, PNG, PDF, Word"
              value={formik.values?.attachment?.name as any}
              height={"120px"}
              accept="all"
              content="Drag & Drop attachment Or Browse Files"
              onClick={(file) => {
                const curFile = file?.item(0);
                formik.setFieldValue("attachment", curFile || undefined);
              }}
            />
          </>
        )}
      </Flex>
    </SideSheetDrawer>
  );
};

export default SendBulkEmailModal;
